/* eslint-disable @typescript-eslint/no-explicit-any */
import { WIDGET_IDS, WIDGET_NAMES } from "@/constants/widget";
import axiosInstance from '../config/axiosConfig';
import {
  FetchWidgetsParams,
  WidgetsApiResponse
} from '../types/widgets.type';
import { PaginatedResponse } from '../types';
import { StatusOverTimeRow } from '../types/widgets.type';
import type { WidgetsTableRow } from '../types/widgets.type';

export async function fetchWidgetsData({
  startDate,
  endDate,
  previousStartDate,
  previousEndDate,
  queueMicrosoftIds,
  agentMicrosoftIds,
  groupIds,
  widgetIds,
  specificDateRanges,
  callId,
  widgetPagination,
  widgetSort
}: FetchWidgetsParams, options?: { signal?: AbortSignal }): Promise<WidgetsApiResponse> {
  const widgets: {
    widgetId: number;
    queueMicrosoftIds?: string[];
    agentMicrosoftIds?: string[];
    groupIds?: number[];
    previousEndDate?: string;
    previousStartDate?: string;
    startDate?: string;
    endDate?: string;
    callId?: string;
    page?: number;
    limit?: number;
    sort?: 'asc' | 'desc';
  }[] = [
      { widgetId: WIDGET_IDS.TOTAL_INBOUND_CALLS, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.TOTAL_OUTBOUND_CALLS, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.TOTAL_INBOUND_CALLS_HANDLED, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.TOTAL_INBOUND_CALLS_MISSED, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.TOTAL_OUTBOUND_CALLS_HANDLED, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.TOTAL_OUTBOUND_CALLS_MISSED, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.TOTAL_INTERNAL_CALLS, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.TOTAL_INBOUND_DIRECT_CALLS, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.TOTAL_ABANDONED_CALLS, queueMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.TOTAL_INBOUND_TRANSFERRED_CALLS, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.TOTAL_TRANSFERRED_CALLS, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.TOTAL_FORWARDED_CALLS, queueMicrosoftIds, agentMicrosoftIds, groupIds },
      { widgetId: WIDGET_IDS.TRANSFERRED_RATE, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.SLA, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.CALL_ABANDONED_RATE, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.AVERAGE_WAITING_TIME, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.AVERAGE_HANDLE_TIME, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.AVERAGE_SPEED_OF_ANSWER, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.TOTAL_ACTIVE_AGENTS, queueMicrosoftIds, agentMicrosoftIds, groupIds },
      { widgetId: WIDGET_IDS.TOP_PERFORMER, queueMicrosoftIds, agentMicrosoftIds, groupIds },
      { widgetId: WIDGET_IDS.AVERAGE_CALLS_PER_AGENT, queueMicrosoftIds, agentMicrosoftIds, groupIds },
      { widgetId: WIDGET_IDS.CALL_VOLUME, queueMicrosoftIds, agentMicrosoftIds, groupIds },
      { widgetId: WIDGET_IDS.INBOUND_STATUS_DISTRIBUTION, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.INBOUND_DIRECT_STATUS_DISTRIBUTION, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.OUTBOUND_STATUS_DISTRIBUTION, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.CALLS_HANDLED_BY_QUEUE, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.SLA_RANKING, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate, ...(widgetSort?.[WIDGET_IDS.SLA_RANKING] ? { sort: widgetSort[WIDGET_IDS.SLA_RANKING] } : {}) },
      { widgetId: WIDGET_IDS.STATUS_OVER_TIME, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.CALL_VOLUME_TREND, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.CALL_STATUS_DISTRIBUTION_TREND, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.AVERAGE_HANDLING_TIME_TREND, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.AVERAGE_WAITING_TIME_TREND, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.AVERAGE_WAITING_TIME_TREND_INBOUND_DIRECT, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.AVERAGE_HANDLING_TIME_TREND_INBOUND_DIRECT, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.AVERAGE_SPEED_OF_ANSWER_INBOUND_DIRECT, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.SLA_TREND, queueMicrosoftIds, agentMicrosoftIds, groupIds },
      { widgetId: WIDGET_IDS.SLA_BY_CALL_QUEUE, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.CALL_DETAIL_OVERVIEW, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.AGENT_PERFORMANCE, queueMicrosoftIds, agentMicrosoftIds, groupIds },
      { widgetId: WIDGET_IDS.CALL_JOURNEY, callId },
      { widgetId: WIDGET_IDS.AVERAGE_CALLS_BY_AGENTS, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.CALL_VOLUME_DISTRIBUTION, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
      { widgetId: WIDGET_IDS.AVERAGE_CALLS_PER_AGENT_CQ, queueMicrosoftIds, agentMicrosoftIds, groupIds, previousEndDate, previousStartDate },
    ];

  specificDateRanges?.forEach(specificDateRange => {
    const needToUpdateWidgets = widgets.filter(widget => specificDateRange.widgetIds.includes(widget.widgetId));
    needToUpdateWidgets.forEach(widget => {
      widget.startDate = specificDateRange.startDate;
      widget.endDate = specificDateRange.endDate;
      widget.previousStartDate = specificDateRange.previousStartDate;
      widget.previousEndDate = specificDateRange.previousEndDate;
    });
  });

  // Apply per-widget pagination if provided
  if (widgetPagination) {
    widgets.forEach(w => {
      const p = widgetPagination[w.widgetId];
      if (p) {
        w.page = p.page;
        w.limit = p.limit;
      }
    });
  }

  // Apply per-widget sort if provided
  if (widgetSort) {
    widgets.forEach(w => {
      const s = widgetSort[w.widgetId];
      if (s) {
        w.sort = s as 'asc' | 'desc';
      }
    });
  }

  const payload = {
    startDate,
    endDate,
    widgets: widgets.filter((widget) => widgetIds.includes(widget.widgetId)),
  };

  const response = await axiosInstance.post<{ data: { widgetData: any[] } }>(
    `/widgets/data`,
    payload,
    { signal: options?.signal }
  );
  const widgetsData = response.data?.data?.widgetData || [];

  // Helper to get widget result by name
  const getWidget = (name: string) => widgetsData.find((w: any) => w.widgetName === name)?.result;

  // Helper to group slaTrend data by time
  const groupSlaTrendData = (data: any[] | undefined): any[] => {
    if (!data || !Array.isArray(data)) return [];

    const grouped = data.reduce((acc, item) => {
      const timeKey = item.time;
      if (!acc[timeKey]) {
        acc[timeKey] = { time: timeKey };
      }

      // Merge all properties from the item
      Object.keys(item).forEach(key => {
        if (key !== 'time') {
          acc[timeKey][key] = item[key];
        }
      });

      return acc;
    }, {} as Record<string, any>);

    return (Object.values(grouped) as any[]).sort((a: any, b: any) => new Date(a.time).getTime() - new Date(b.time).getTime());
  };

  // Map REST response to WidgetsApiResponse
  const mapped: WidgetsApiResponse = {
    totalAgents: getWidget('TotalActiveAgents')?.totalActiveAgents ?? 0,
    comparisonPercentageTAW: getWidget('TotalActiveAgents')?.comparisonPercentage ?? null,
    topPerformer: getWidget('TopPerformer')?.agentName ?? '',
    averageCallsPerAgent: getWidget('AverageCallsPerAgent')?.averageCalls ?? 0,
    comparisonPercentageACP: ((getWidget('AverageCallsPerAgent') as { data?: Array<{ averageCalls?: number }> } | undefined)?.data?.reduce((sum, item) => sum + (item.averageCalls ?? 0), 0) ?? 0) / 100,
    averageCallsPerAgentCQ: getWidget('AverageCallsPerAgentCallQueueInsight')?.averageCalls ?? 0,
    averageHandlingTime: getWidget('AverageHandleTime')?.averageHandleTime ?? 0,
    comparisonPercentageAHT: getWidget('AverageHandleTime')?.comparisonPercentage ?? null,
    averageSpeedOfAnswer: getWidget('AverageSpeedOfAnswer')?.averageSpeedOfAnswer ?? 0,
    comparisonPercentageASO: getWidget('AverageSpeedOfAnswer')?.comparisonPercentage ?? null,
    averageSpeedOfAnswerInboundDirect: getWidget('AverageSpeedOfAnswerInboundDirect')?.averageSpeedOfAnswer ?? 0,
    comparisonPercentageASOInboundDirect: getWidget('AverageSpeedOfAnswerInboundDirect')?.comparisonPercentage ?? null,
    averageWaitingTime: getWidget('AverageWaitingTime')?.averageWaitingTime ?? 0,
    comparisonPercentageAWT: getWidget('AverageWaitingTime')?.comparisonPercentage ?? null,
    averageWaitingTimeInboundDirect: getWidget('AverageWaitingTimeInboundDirect')?.averageWaitingTime ?? 0,
    comparisonPercentageAWTInboundDirect: getWidget('AverageWaitingTimeInboundDirect')?.comparisonPercentage ?? null,
    averageHandlingTimeInboundDirect: getWidget('AverageHandleTimeInboundDirect')?.averageHandleTime ?? 0,
    comparisonPercentageAHTInboundDirect: getWidget('AverageHandleTimeInboundDirect')?.comparisonPercentage ?? null,
    slaRanking: (((getWidget('SLARanking') as { data?: Array<{ agentName: string; slaPercentage: number }> } | undefined)?.data) ?? []).map((item) => ({
      agent: item.agentName,
      sla: item.slaPercentage,
    })),
    statusOverTime: (((getWidget('StatusOverTime') as { data?: Array<{ agentName: string } & Record<string, unknown>> } | undefined)?.data) ?? []).map((item) => {
      const { agentName, ...statusFields } = item as { agentName: string } & Record<string, unknown>;
      return {
        agent: agentName,
        agentName,
        ...statusFields,
      };
    }),
    agentPerformanceTable: (((getWidget('AgentPerformance') as { data?: Array<Record<string, unknown>> } | undefined)?.data) ?? []).map((item, idx: number) => ({
      key: idx,
      agent: (item as any).agentName,
      agentName: (item as any).agentName ?? '',
      totalInboundCallsHandled: (item as any).totalInboundCallsHandled ?? 0,
      totalInboundCallsMissed: (item as any).totalInboundCallsMissed ?? (item as any).inboundMissed ?? 0,
      totalOutboundCallsHandled: (item as any).totalOutboundCallsHandled ?? 0,
      totalOutboundCallsMissed: (item as any).totalOutboundCallsMissed ?? (item as any).outboundMissed ?? 0,
      totalInboundDirectCallsHandled: (item as any).totalInboundDirectCallsHandled ?? 0,
      totalInboundDirectCallsMissed: (item as any).totalInboundDirectCallsMissed ?? 0,
      totalMissedDirectCalls: (item as any).totalMissedDirectCalls ?? 0, // legacy
      totalForwarded: (item as any).totalForwarded ?? 0, // legacy
      totalTransferred: (item as any).totalTransferred ?? 0, // legacy
      totalTransferredCalls: (item as any).totalTransferredCalls ?? (item as any).totalTransferred ?? 0,
      totalInternal: (item as any).totalInternal ?? 0, // legacy
      totalInternalCalls: (item as any).totalInternalCalls ?? (item as any).totalInternal ?? 0,
      totalVoicemailCalls: (item as any).voicemail ?? (item as any).totalVoicemailCalls ?? 0,
      totalMissedCalls: (item as any).totalMissedCalls ?? 0, // legacy
      averageHandledTime: (item as any).averageHandledTime ?? 0,
      averageWaitingTime: (item as any).averageWaitingTime ?? 0,
      averageSpeedOfAnswer: (item as any).averageSpeedOfAnswer ?? 0,
      slaPercentage: (item as any).sla?.toString() ?? '',
      callQueue: (item as any).callQueue ?? '',
      inbound: (item as any).totalInboundCallsHandled ?? 0,
      outbound: (item as any).totalOutboundCallsHandled ?? 0,
      missed: (item as any).totalMissedCalls ?? 0,
      sla: Number((item as any).sla ?? 0),
    })),
    trendsInsightsData: {
      callVolumeTrend: getWidget('CallVolumeTrend')?.data ?? null,
      callStatusDistributionTrend: getWidget('CallStatusDistributionTrend')?.data ?? null,
      averageHandlingTimeTrend: getWidget('AverageHandlingTimeTrend')?.data ?? null,
      averageWaitingTimeTrend: getWidget('AverageWaitingTimeTrend')?.data ?? null,
      averageCallsPerAgentTrend: getWidget('AverageCallsPerAgentTrend')?.data ?? null,
      slaTrend: groupSlaTrendData((getWidget('SLATrend') as { data?: Array<{ time: string;[k: string]: unknown }> } | undefined)?.data) as unknown as any,
      slaByCallQueue: getWidget('SLAByCallQueue')?.data ?? null,
    },
    callOverviewData: {
      totalInboundCalls: getWidget(WIDGET_NAMES.TOTAL_INBOUND_CALLS),
      totalInboundDirectCalls: getWidget(WIDGET_NAMES.TOTAL_INBOUND_DIRECT_CALLS),
      totalOutboundCalls: getWidget(WIDGET_NAMES.TOTAL_OUTBOUND_CALLS),
      totalInboundCallsHandled: getWidget(WIDGET_NAMES.TOTAL_INBOUND_CALLS_HANDLED),
      totalInboundCallsMissed: getWidget(WIDGET_NAMES.TOTAL_INBOUND_CALLS_MISSED),
      totalOutboundCallsHandled: getWidget(WIDGET_NAMES.TOTAL_OUTBOUND_CALLS_HANDLED),
      totalOutboundCallsMissed: getWidget(WIDGET_NAMES.TOTAL_OUTBOUND_CALLS_MISSED),
      totalInternalCalls: getWidget(WIDGET_NAMES.TOTAL_INTERNAL_CALLS),
      totalAbandonedCalls: getWidget(WIDGET_NAMES.TOTAL_ABANDONED_CALLS),
      totalTransferredCalls: getWidget(WIDGET_NAMES.TOTAL_TRANSFERRED_CALLS),
      totalInboundTransferredCalls: getWidget(WIDGET_NAMES.TOTAL_INBOUND_TRANSFERRED_CALLS),
      totalForwardedCalls: getWidget(WIDGET_NAMES.TOTAL_FORWARDED_CALLS),
      transferredRate: getWidget(WIDGET_NAMES.TRANSFERRED_RATE),
      sla: getWidget(WIDGET_NAMES.SLA),
      callAbandonedRate: getWidget(WIDGET_NAMES.CALL_ABANDONED_RATE),
      averageWaitingTime: getWidget(WIDGET_NAMES.AVERAGE_WAITING_TIME),
      averageHandleTime: getWidget(WIDGET_NAMES.AVERAGE_HANDLE_TIME),
      averageSpeedOfAnswer: getWidget(WIDGET_NAMES.AVERAGE_SPEED_OF_ANSWER),
      inboundStatusDistribution: getWidget(WIDGET_NAMES.INBOUND_STATUS_DISTRIBUTION),
      inboundDirectStatusDistribution: getWidget(WIDGET_NAMES.INBOUND_DIRECT_STATUS_DISTRIBUTION),
      outboundStatusDistribution: getWidget(WIDGET_NAMES.OUTBOUND_STATUS_DISTRIBUTION),
      callsHandledByQueue: getWidget(WIDGET_NAMES.CALLS_HANDLED_BY_QUEUE)?.data ?? [],
      callVolume: getWidget(WIDGET_NAMES.CALL_VOLUME)?.data ?? [],
      callDetailOverview: getWidget(WIDGET_NAMES.CALL_DETAIL_OVERVIEW)?.data ?? [],
      callVolumeDistribution: getWidget(WIDGET_NAMES.CALL_VOLUME_DISTRIBUTION)?.data ?? [],
    },
    callJourney: getWidget('CallJourney')?.callJourneyDetailDtos ?? []
  };

  // If server returns paginated payloads for specific widgets, map them as well
  const slaRankingRaw = widgetsData.find((w: { widgetName: string }) => w.widgetName === WIDGET_NAMES.SLA_RANKING)?.result as { data?: any[]; total?: number; page?: number; limit?: number; totalPages?: number } | undefined;
  if (slaRankingRaw && slaRankingRaw?.data && typeof slaRankingRaw?.total === 'number') {
    mapped.slaRankingPaged = {
      data: (slaRankingRaw.data || []).map((item: { agentName: string; slaPercentage: number }) => ({ agent: item.agentName, sla: item.slaPercentage })),
      total: slaRankingRaw.total,
      page: slaRankingRaw.page ?? 1,
      limit: slaRankingRaw.limit ?? (widgetPagination?.[WIDGET_IDS.SLA_RANKING]?.limit || 10),
      totalPages: slaRankingRaw.totalPages ?? Math.ceil((slaRankingRaw.total || 0) / (slaRankingRaw.limit || 10))
    };
  }

  const statusOverTimeRaw = widgetsData.find((w: { widgetName: string }) => w.widgetName === WIDGET_NAMES.STATUS_OVER_TIME)?.result as { data?: any[]; total?: number; page?: number; limit?: number; totalPages?: number } | undefined;
  if (statusOverTimeRaw && statusOverTimeRaw?.data && typeof statusOverTimeRaw?.total === 'number') {
    mapped.statusOverTimePaged = {
      data: (statusOverTimeRaw.data || []).map((item: Record<string, unknown>) => {
        const { agentName, ...statusFields } = item as { agentName: string } & Record<string, unknown>;
        return {
          agent: agentName,
          agentName,
          ...statusFields,
        };
      }),
      total: statusOverTimeRaw.total,
      page: statusOverTimeRaw.page ?? 1,
      limit: statusOverTimeRaw.limit ?? (widgetPagination?.[WIDGET_IDS.STATUS_OVER_TIME]?.limit || 10),
      totalPages: statusOverTimeRaw.totalPages ?? Math.ceil((statusOverTimeRaw.total || 0) / (statusOverTimeRaw.limit || 10))
    };
  }

  return mapped;
}

export async function fetchAgentPerformanceTable(params: {
  startTime: string;
  endTime: string;
  agentMicrosoftIds: string[] | null;
  agentNameSearchText?: string | null;
  sortTable?: { columnName: string; columnValue: 'asc' | 'desc' } | null;
  page?: number | null;
  limit?: number | null;
}, options?: { signal?: AbortSignal }): Promise<{ data: WidgetsTableRow[]; total: number }> {
  const payload = {
    startTime: params.startTime,
    endTime: params.endTime,
    agentMicrosoftIds: params.agentMicrosoftIds,
    agentNameSearchText: params.agentNameSearchText ?? null,
    sortTable: params.sortTable ?? undefined,
    page: params.page ?? 1,
    limit: params.limit ?? null,
  };
  const res = await axiosInstance.post<{ data: any[]; total?: number }>(`/widgets/agent-performance`, payload, { signal: options?.signal });
  const rows = (res.data?.data ?? []).map((item, idx: number) => ({
    key: idx,
    agent: (item as any).agentName,
    agentName: (item as any).agentName ?? '',
    totalInboundCallsHandled: (item as any).totalInboundCallsHandled ?? 0,
    totalInboundCallsMissed: (item as any).totalInboundCallsMissed ?? 0,
    totalOutboundCallsHandled: (item as any).totalOutboundCallsHandled ?? 0,
    totalOutboundCallsMissed: (item as any).totalOutboundCallsMissed ?? 0,
    totalInboundDirectCallsHandled: (item as any).totalInboundDirectCallsHandled ?? 0,
    totalInboundDirectCallsMissed: (item as any).totalInboundDirectCallsMissed ?? 0,
    totalTransferredCalls: (item as any).totalTransferredCalls ?? (item as any).totalTransferred ?? 0,
    totalInternalCalls: (item as any).totalInternalCalls ?? (item as any).totalInternal ?? 0,
    totalVoicemailCalls: (item as any).voicemail ?? (item as any).totalVoicemailCalls ?? 0,
    averageHandledTime: (item as any).averageHandledTime ?? 0,
    averageWaitingTime: (item as any).averageWaitingTime ?? 0,
    averageSpeedOfAnswer: (item as any).averageSpeedOfAnswer ?? 0,
    sla: Number((item as any).sla ?? 0),
    callQueue: (item as any).callQueue ?? '',
    totalMissedCalls: (item as any).totalInboundCallsMissed ?? 0,
    totalMissedDirectCalls: (item as any).totalInboundDirectCallsMissed ?? 0,
    totalForwarded: (item as any).totalForwarded ?? 0,
    totalTransferred: (item as any).totalTransferred ?? 0,
    totalInternal: (item as any).totalInternal ?? 0,
  })) as WidgetsTableRow[];
  return { data: rows, total: res.data?.total ?? rows.length };
}

// Dedicated endpoint: SLA Ranking (server-side pagination, reload only this widget)
export async function fetchSlaRankingPaged({
  startDate,
  endDate,
  previousStartDate,
  previousEndDate,
  queueMicrosoftIds,
  agentMicrosoftIds,
  groupIds,
  page,
  limit,
  sort
}: {
  startDate: string;
  endDate: string;
  previousStartDate?: string;
  previousEndDate?: string;
  queueMicrosoftIds?: string[] | null;
  agentMicrosoftIds?: string[] | null;
  groupIds?: number[] | null;
  page: number;
  limit: number;
  sort?: 'asc' | 'desc';
}, options?: { signal?: AbortSignal }): Promise<PaginatedResponse<{ agent: string; sla: number }>> {
  const payload = {
    startDate,
    endDate,
    widgets: [
      {
        widgetId: WIDGET_IDS.SLA_RANKING,
        queueMicrosoftIds: queueMicrosoftIds ?? undefined,
        agentMicrosoftIds: agentMicrosoftIds ?? undefined,
        groupIds: groupIds ?? undefined,
        previousStartDate,
        previousEndDate,
        page,
        limit,
        ...(sort ? { sort } : {})
      }
    ]
  };

  const res = await axiosInstance.post<any>(`/widgets/sla-ranking`, payload, { signal: options?.signal });
  // Normalize various response shapes: { data: [...] , total } or { data: { data: [...], total, ... } } or { result: { ... } }
  const top = res?.data ?? {};
  const container = (top?.data && !Array.isArray(top.data)) ? top.data : (top?.result && !Array.isArray(top.result)) ? top.result : top;
  const rawData: any[] = Array.isArray(top?.data)
    ? top.data
    : Array.isArray(container?.data)
      ? container.data
      : [];
  const mappedData = (rawData || []).map((item: any) => ({
    agent: item.agentName ?? item.agent ?? '',
    sla: item.slaPercentage ?? item.sla ?? 0
  }));
  const total = Number(container?.total ?? top?.total ?? 0);
  const resp: PaginatedResponse<{ agent: string; sla: number }> = {
    data: mappedData,
    total,
    page: Number(container?.page ?? top?.page ?? page ?? 1),
    limit: Number(container?.limit ?? top?.limit ?? limit ?? 10),
    totalPages: Number(container?.totalPages ?? top?.totalPages ?? Math.ceil((total || 0) / (Number(container?.limit ?? top?.limit ?? limit ?? 10))))
  };
  return resp;
}

// Dedicated endpoint: Status Over Time (server-side pagination, reload only this widget)
export async function fetchStatusOverTimePaged({
  startDate,
  endDate,
  previousStartDate,
  previousEndDate,
  queueMicrosoftIds,
  agentMicrosoftIds,
  groupIds,
  page,
  limit
}: {
  startDate: string;
  endDate: string;
  previousStartDate?: string;
  previousEndDate?: string;
  queueMicrosoftIds?: string[] | null;
  agentMicrosoftIds?: string[] | null;
  groupIds?: number[] | null;
  page: number;
  limit: number;
}, options?: { signal?: AbortSignal }): Promise<PaginatedResponse<StatusOverTimeRow>> {
  const payload = {
    startDate,
    endDate,
    widgets: [
      {
        widgetId: WIDGET_IDS.STATUS_OVER_TIME,
        queueMicrosoftIds: queueMicrosoftIds ?? undefined,
        agentMicrosoftIds: agentMicrosoftIds ?? undefined,
        groupIds: groupIds ?? undefined,
        previousStartDate,
        previousEndDate,
        page,
        limit
      }
    ]
  };
  const res = await axiosInstance.post<any>(`/widgets/status-over-time`, payload, { signal: options?.signal });
  // Normalize shapes as above
  const top = res?.data ?? {};
  const container = (top?.data && !Array.isArray(top.data)) ? top.data : (top?.result && !Array.isArray(top.result)) ? top.result : top;
  const rawData: any[] = Array.isArray(top?.data)
    ? top.data
    : Array.isArray(container?.data)
      ? container.data
      : [];
  const mappedData = (rawData || []).map((item: any): StatusOverTimeRow => {
    const { agentName, ...rest } = item ?? {};
    return {
      agent: agentName ?? item?.agent ?? '',
      agentName: agentName ?? item?.agent ?? '',
      ...rest
    };
  });
  const total = Number(container?.total ?? top?.total ?? 0);
  const resp: PaginatedResponse<StatusOverTimeRow> = {
    data: mappedData,
    total,
    page: Number(container?.page ?? top?.page ?? page ?? 1),
    limit: Number(container?.limit ?? top?.limit ?? limit ?? 10),
    totalPages: Number(container?.totalPages ?? top?.totalPages ?? Math.ceil((total || 0) / (Number(container?.limit ?? top?.limit ?? limit ?? 10))))
  };
  return resp;
}

export async function fetchKeyMetrics({
  start,
  end,
  previousStart,
  previousEnd,
  statusTrendStart,
  statusTrendEnd,
  offsetInMinutes,
  queueIds,
  agentIds,
  directAgentIds
}: {
  start: string;
  end: string;
  previousStart: string;
  previousEnd: string;
  statusTrendStart: string;
  statusTrendEnd: string;
  offsetInMinutes: number;
  queueIds: string[];
  agentIds: string[];
  directAgentIds: string[];
}, options?: { signal?: AbortSignal }) {
  const payload = {
    start,
    end,
    previousStart,
    previousEnd,
    statusTrendStart,
    statusTrendEnd,
    offsetInMinutes,
    queueIds,
    agentIds,
    directAgentIds
  };
  const response = await axiosInstance.post('/widgets/key-metrics', payload, { signal: options?.signal });
  return response.data;
}

export async function fetchKeyMetricsBulk({
  start,
  end,
  previousStart,
  previousEnd,
  statusTrendStart,
  statusTrendEnd,
  offsetInMinutes,
  queueIds,
  agentIds,
  directAgentIds
}: {
  start: string;
  end: string;
  previousStart: string;
  previousEnd: string;
  statusTrendStart: string;
  statusTrendEnd: string;
  offsetInMinutes: number;
  queueIds: string[];
  agentIds: string[];
  directAgentIds: string[];
}, widgets: number[], options?: { signal?: AbortSignal }): Promise<Record<string, unknown>> {
  const payload = {
    parameters: {
      start,
      end,
      previousStart,
      previousEnd,
      statusTrendStart,
      statusTrendEnd,
      offsetInMinutes,
      queueIds,
      agentIds,
      directAgentIds
    },
    widgets,
    version: 2
  };
  const response = await axiosInstance.post('/widgets/key-metrics/bulk', payload, { signal: options?.signal });
  return response.data as Record<string, unknown>;
}

export async function fetchKeyMetricWidget({
  start,
  end,
  previousStart,
  previousEnd,
  statusTrendStart,
  statusTrendEnd,
  offsetInMinutes,
  queueIds,
  agentIds,
  directAgentIds
}: {
  start: string;
  end: string;
  previousStart: string;
  previousEnd: string;
  statusTrendStart: string;
  statusTrendEnd: string;
  offsetInMinutes: number;
  queueIds: string[];
  agentIds: string[];
  directAgentIds: string[];
}, widget: number, options?: { signal?: AbortSignal }) {
  const payload = {
    start,
    end,
    previousStart,
    previousEnd,
    statusTrendStart,
    statusTrendEnd,
    offsetInMinutes,
    queueIds,
    agentIds,
    directAgentIds
  };
  const response = await axiosInstance.post(`/widgets/key-metrics?widget=${widget}`, payload, { signal: options?.signal });
  return response.data;
}
